// Reader-side sync and preference types (SPEC "Sync anchors", "Reader UX").
// These are src-only: they describe runtime rendering state derived from the
// shared anchor data, not anything stored in Convex.

import type { AnchorPair } from "@bindery/convex-retells/model/anchor";
import type { Bookmark } from "@bindery/convex-retells/model/bookmark";
import type { RetellingLocation } from "@bindery/convex-retells/model/location";
import type { ReadMark } from "@bindery/convex-retells/model/readMark";

/** The two panes of the reading view. */
export type PaneId = "retelling" | "original";

/** How the two panes are aligned; both modes derive from the same anchors. */
export type SyncMode = "interpolated" | "hard";

/**
 * Which pane currently leads scrolling — derived from where scroll events
 * originate (hover is only a secondary signal; it never fires on touch).
 * null until the reader interacts.
 */
export type SyncLeader = PaneId | null;

/**
 * One interpolation segment between consecutive anchor pairs, resolved to
 * rendered pixel offsets per pane. The follower's position is piecewise-
 * linear between `startPx` and `endPx`. A null pair marks the implicit
 * document-start / document-end sentinel.
 */
export interface SyncSegment {
  from: AnchorPair | null;
  to: AnchorPair | null;
  startPx: Record<PaneId, number>;
  endPx: Record<PaneId, number>;
}

/**
 * Per-pane font size (px), identical by default; `linked` moves both
 * together. Persisted to localStorage.
 */
export interface FontSizePrefs {
  retelling: number;
  original: number;
  linked: boolean;
}

/** Font-size default shared by the chapter and matter reading views. */
export const DEFAULT_FONT: FontSizePrefs = {
  retelling: 19,
  original: 19,
  linked: true,
};

/** Reading position, round-tripped through the ?loc= URL param. */
export interface ReadingProgress {
  bookSlug: string;
  chapterNumber: number;
  location: RetellingLocation;
}

/**
 * A locally-stored bookmark or current-position record: the Convex `Bookmark`
 * minus its server-set fields (the owner only exists once signed in). The
 * distributive `Omit` preserves the `kind` discriminant, so a localStorage
 * record and a synced Convex row differ only by those fields — which is
 * exactly what `bookmarks.mergeLocal` unions on sign-in (SPEC "Auth").
 */
type WithoutServerFields<B> = B extends unknown
  ? Omit<B, "workosId" | "updatedAt">
  : never;
export type LocalBookmark = WithoutServerFields<Bookmark>;

/**
 * A locally-stored "I finished this volume" mark — the Convex `ReadMark` minus
 * its server-set fields, on the same principle as `LocalBookmark`.
 * localStorage is the always-on store (an anonymous reader gets the whole
 * feature); the Convex rows are the signed-in reader's cross-device copy, and
 * `src/reader/read-marks.ts` reads the union of the two.
 */
export type LocalReadMark = WithoutServerFields<ReadMark>;
